import React from 'react'
import styled from 'styled-components'
import { Location } from '@reach/router'

import { ContextLink } from '@/components/common/context-link'
import { gaEvents } from '@/utils/ga-events'
import { Button } from '@/components/common/create-app-button'
import { TitleLink } from '@/types/title-link'
import { useTheme } from '@/styles'
import {
  docsClipsSidebarLinks,
  docsEndpointLinks,
  docsOptionalLinks,
  docsResourceLinks,
  docsSchemaLinks,
  docsSdkLinks,
  docsTrustAndSafetyLinks,
} from '@/config/links'

import { Dropdown } from './dropdown'

const Container = styled.div`
  position: relative;
  display: flex;
  flex-shrink: 0;
  width: ${({ theme }) => theme.sidebarWidth}px;
  margin-right: 30px;
`

const FixedContainer = styled.div`
  position: sticky;
  top: 96px;

  display: flex;
  flex-direction: column;
  width: ${({ theme }) => theme.sidebarWidth}px;
  max-height: calc(100vh - 100px);
  margin-bottom: 20px;
`

const Navigation = styled.nav`
  max-height: 605px;
  padding: 20px 0 0 30px;
  margin-bottom: 15px;
  border: 0 solid black;
  font-size: 15px;
  background-color: rgba(225, 225, 225, 0.3);
  overflow-y: auto;

  > ul {
    margin-top: 0;
    margin-bottom: 20px;
  }

  ul {
    list-style: none;
    padding-left: 0;
    font-size: 15px;
  }

  li {
    margin-bottom: 6px;
  }

  a {
    line-height: 20px;
    padding-left: 15px;
    text-decoration: none;
    border: none;
    box-decoration-break: clone;
    color: ${({ theme }) => theme.palette.giphyCharcoal};

    &:hover {
      color: ${({ theme }) => theme.palette.giphyLightBlue};
    }
  }
`

const Heading = styled.h6`
  font-weight: bold;
  line-height: 20px;
  margin-bottom: 6px;
`

const StyledButton = styled(Button)`
  width: auto;
  margin: 0;
  line-height: 36px;
`

const matchHashCollection = (hash: string, collection: TitleLink[]) =>
  Boolean(hash) && collection.some((item: TitleLink) => item.link.includes(hash))

const matchPathCollection = (path: string, collection: TitleLink[]) =>
  Boolean(path) && collection.some((item: TitleLink) => item.link === path)

const matchPaths = (path: string, to: string) =>
  path.replace(/\/|(#$)/g, '') === to.replace(/\/|(#$)/g, '')

const Link =
  (route: string): React.FC<{ to: string }> =>
  ({ to, children }) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { palette } = useTheme()

    return (
      <ContextLink
        to={to}
        getProps={() => ({
          style: matchPaths(route, to)
            ? { color: palette.giphyLightBlue, fontWeight: 'bold' }
            : null,
        })}
      >
        {children}
      </ContextLink>
    )
  }

export const DocsSidebar = ({ route }: { route: string }) => {
  const SidebarLink = Link(route)

  return (
    <Container>
      <FixedContainer>
        <Navigation>
          <Heading>GIPHY SDK</Heading>
          <ul>
            {docsSdkLinks.map(({ title, link }, index) => (
              <li key={index}>
                <SidebarLink to={link}>{title}</SidebarLink>
              </li>
            ))}
          </ul>

          <Heading>GIPHY API</Heading>
          <Location>
            {({ location }) => (
              <ul>
                <li>
                  <SidebarLink to="/docs/api#quick-start-guide">API Quickstart Guide</SidebarLink>
                </li>
                <li>
                  <Dropdown
                    title="GIF & Sticker Endpoints"
                    isOpen={matchHashCollection(location.hash, docsEndpointLinks)}
                    isActive={matchPathCollection(route, docsEndpointLinks)}
                  >
                    <ul>
                      {docsEndpointLinks.map(({ title, link }, index) => (
                        <li key={index}>
                          <SidebarLink to={link}>{title}</SidebarLink>
                        </li>
                      ))}
                    </ul>
                  </Dropdown>
                </li>
                <li>
                  <Dropdown
                    title="Schema Definitions"
                    isOpen={matchHashCollection(location.hash, docsSchemaLinks)}
                    isActive={matchPathCollection(route, docsSchemaLinks)}
                  >
                    <ul>
                      {docsSchemaLinks.map(({ title, link }, index) => (
                        <li key={index}>
                          <SidebarLink to={link}>{title}</SidebarLink>
                        </li>
                      ))}
                    </ul>
                  </Dropdown>
                </li>
                <li>
                  <SidebarLink to="/docs/api#response-codes">Response Code</SidebarLink>
                </li>
              </ul>
            )}
          </Location>

          <Heading>GIPHY Clips</Heading>
          <Location>
            {({ location }) => (
              <ul>
                {docsClipsSidebarLinks.map((item) => (
                  <li key={item.link}>
                    {item.children ? (
                      <Dropdown
                        title={item.title}
                        isOpen={matchHashCollection(location.hash, docsEndpointLinks)}
                        isActive={matchPathCollection(route, docsEndpointLinks)}
                      >
                        <ul>
                          {item.children.map((subItem) => (
                            <li key={subItem.link}>
                              <SidebarLink to={subItem.link}>{subItem.title}</SidebarLink>
                            </li>
                          ))}
                        </ul>
                      </Dropdown>
                    ) : (
                      <SidebarLink to={item.link}>{item.title}</SidebarLink>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Location>

          <Heading>Optional Settings</Heading>
          <ul>
            {docsOptionalLinks.map(({ title, link }, index) => (
              <li key={index}>
                <SidebarLink to={link}>{title}</SidebarLink>
              </li>
            ))}
          </ul>
          <Heading>Trust And Safety</Heading>
          <ul>
            {docsTrustAndSafetyLinks.map(({ title, link }, index) => (
              <li key={index}>
                <SidebarLink to={link}>{title}</SidebarLink>
              </li>
            ))}
          </ul>

          <Heading>Resources</Heading>
          <ul>
            {docsResourceLinks.map(({ title, link }, index) => (
              <li key={index}>
                <SidebarLink to={link}>{title}</SidebarLink>
              </li>
            ))}
          </ul>
        </Navigation>
        <StyledButton href="/dashboard/?create=true" onClick={gaEvents.createNewApp}>
          Create an App
        </StyledButton>
      </FixedContainer>
    </Container>
  )
}
